import { evidenceSidebarOpenAtom } from '@/atoms/evidenceSidebarAtoms';
import { useIsNewSessionListEnabled } from '@/features/flags';
import { useEhrLaunchSessionId } from '@/hooks/integrations/useEhrLaunchSessionId';
import { useIsEhrLaunch } from '@/hooks/integrations/useIsEhrLaunch';
import { useAuth } from '@/hooks/useAuth';
import { useSessionsPanelTracking } from '@/models/analytics/features/sessions-panel-tracking';
import { type ImperativePanelHandle } from '@heidi/design-system/resizable';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import {
  sessionListColdRestorePendingAtom,
  sessionListCollapsedAtom,
  useEhrLaunchSessionRedirect,
  useSidebarSessionListBridge,
} from '@heidi/session';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useProgrammaticPanelResize } from './hooks/use-programmatic-panel-resize';
import { useSessionsSplitClose } from './hooks/useSessionsSplitClose';
import { useLastViewedSessionId } from './sessions-panel-last-viewed';
import {
  computeMaxSplitPx,
  computeSplitPercent,
  DEFAULT_SPLIT_PX,
  getSplitStorageKey,
  MIN_DETAIL_PX,
  MIN_PANEL_PX,
  readSavedSplitPx,
  SPLIT_MIN_CONTAINER_PX,
} from './sessions-panel-split-storage';

import {
  SessionsPanelContext,
  type PanelActions,
  type SessionsPanelContextValue,
} from './sessions-panel-context-value';

export { SessionsPanelContext, type PanelActions } from './sessions-panel-context-value';

// The panel animates resizes over 300ms (`duration-300`). Persistence is
// suppressed while a programmatic resize (open/close/restore) settles so its
// animation frames aren't mistaken for a user-chosen width. The window is a
// touch longer than the transition to comfortably outlast the final frame.
const PROGRAMMATIC_RESIZE_SETTLE_MS = 350;

// Debounced: the width only feeds the list's `maxSize` (a drag ceiling), which
// tolerates lag — and a window/Evidence resize fires the observer/`onResize`
// every frame, each of which would otherwise re-render the note-editor subtree.
const CONTAINER_MEASURE_DEBOUNCE_MS = 150;

// Re-exported so the page can feed the same floors to each `<ResizablePanel
// minSize>` — `MIN_PANEL_PX` for the list (left), `MIN_DETAIL_PX` for the
// detail (right) so the live drag can never push the detail below its minimum —
// and `computeMaxSplitPx` for the list's live `maxSize` (the drag ceiling).
export { computeMaxSplitPx, MIN_DETAIL_PX, MIN_PANEL_PX };

export const useSessionsPanelControls = ({
  isStableStartupEnabled = false,
  isStableStartupFlagResolved = true,
  hasColdLoadCoverReleased = false,
}: {
  isStableStartupEnabled?: boolean;
  isStableStartupFlagResolved?: boolean;
  /**
   * The cold-load cover is no longer hiding the layout. Startup geometry that
   * has not settled by then has lost its cover, so it hands back to the legacy
   * path rather than holding a detail route at `defaultSize` 0.
   */
  hasColdLoadCoverReleased?: boolean;
} = {}) => {
  const router = useRouter();
  const sessionId = useCurrentSessionId();
  const hasDetail = !!sessionId;

  const panelTracking = useSessionsPanelTracking(hasDetail, sessionId);

  const isEhrLaunch = useIsEhrLaunch();
  // Third clause of the same launch policy as the collapse below and the nulled
  // `panelActions`: with the list withheld, a bare `/scribe/sessions` has nothing
  // to render, so return to the session the launch resolved.
  useEhrLaunchSessionRedirect({
    sessionId,
    launchSessionId: useEhrLaunchSessionId(),
    onNavigate: (href) => void router.replace(href),
  });

  const { isAuthenticated, isLoading: isAuthLoading, user } = useAuth();
  const storageKey = getSplitStorageKey(user?.id);
  const isSplitStorageReady = !isAuthLoading && (!isAuthenticated || Boolean(user?.id));
  const isColdRestorePending = useAtomValue(sessionListColdRestorePendingAtom);
  const leftPanelRef = useRef<ImperativePanelHandle>(null);
  const rightPanelRef = useRef<ImperativePanelHandle>(null);
  const panelGroupRef = useRef<HTMLDivElement | null>(null);
  // The measurement wrapper mounts AFTER this hook's first commit whenever the
  // page is gated — flag provider not settled (loader) or the new-list flag
  // reading its default (null) — so a mount-once effect that reads
  // `panelGroupRef.current` binds nothing: `isContainerMeasured` never flips,
  // and the restore effect's one-shot open runs against null panel refs, leaving
  // the detail pane at its `defaultSize` 0 on deep links, reloads and EHR /
  // notification links. Track the node through a callback ref so the
  // measurement (and, via `isContainerMeasured`, the restore effect) re-run when
  // the wrapper actually appears. The object ref stays in sync for the
  // imperative readers (`getSplitPercent`, the resize hooks, the drag guard).
  const [panelGroupEl, setPanelGroupEl] = useState<HTMLDivElement | null>(null);
  const setPanelGroupRef = useCallback((node: HTMLDivElement | null) => {
    panelGroupRef.current = node;
    setPanelGroupEl(node);
  }, []);
  // The OUTER wrapper's rendered pixel width (list + detail + Evidence), kept
  // current by ResizeObserver. Non-reactive: read by the geometry helpers.
  const containerWidthRef = useRef(0);
  // Synchronous mirrors of the workspace geometry for the imperative save/restore
  // path (which reads on a drag, not on render). The workspace width — wrapper ×
  // share — is what the list ceiling is applied to, so persistence and restore
  // must use it too, or a width saved with Evidence open restores wrong.
  const workspaceSharePctRef = useRef(100);
  const workspaceWidthRef = useRef(0);
  // Reactive outer width + the list+detail group's share of it. The ceiling lives
  // in the inner group, which Evidence (≥20%) shrinks below the wrapper, so it
  // must scale by the share, not the raw wrapper width. Both writes debounce.
  const [containerWidth, setContainerWidth] = useState(0);
  const [workspaceSharePct, setWorkspaceSharePct] = useState(100);
  const debouncedSetContainerWidth = useDebouncedCallback(
    setContainerWidth,
    CONTAINER_MEASURE_DEBOUNCE_MS,
    { leading: true, trailing: true }
  );
  const debouncedSetWorkspaceShare = useDebouncedCallback(
    setWorkspaceSharePct,
    CONTAINER_MEASURE_DEBOUNCE_MS,
    { leading: true, trailing: true }
  );
  // Flips true once the container has a real measurement so the restore effect
  // can re-run and apply the saved/target width, rather than guessing before
  // layout (which previously fell back to a too-wide 40%).
  const [isContainerMeasured, setIsContainerMeasured] = useState(false);
  // Whether the container is wide enough to hold both panels at their minimums.
  // When false the page renders a single pane (list- or detail-only) instead of
  // the split — both floors can't be honoured, so the detail takes priority.
  // Seeded from the viewport so a narrow first paint doesn't flash the split
  // before the first measurement lands.
  const [canFitSplit, setCanFitSplit] = useState(
    () => typeof window === 'undefined' || window.innerWidth >= SPLIT_MIN_CONTAINER_PX
  );
  useSidebarSessionListBridge(canFitSplit);

  // Narrow widths render single-pane inside the wrapper (not a separate
  // subtree), so once bound the observer stays on the live node and keeps
  // `canFitSplit` current across every resize; it re-binds only when the
  // wrapper itself (re)mounts.
  useEffect(() => {
    const el = panelGroupEl;
    if (!el) return;
    const applyWidth = (width: number) => {
      containerWidthRef.current = width;
      workspaceWidthRef.current = (width * workspaceSharePctRef.current) / 100;
      debouncedSetContainerWidth(width);
      setIsContainerMeasured(true);
      setCanFitSplit(width >= SPLIT_MIN_CONTAINER_PX);
    };
    if (el.offsetWidth > 0) applyWidth(el.offsetWidth);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry && entry.contentRect.width > 0) applyWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
    // `debouncedSetContainerWidth` is stable (use-debounce memoizes it), so the
    // observer binds once per wrapper mount despite the dependency.
  }, [panelGroupEl, debouncedSetContainerWidth]);

  // The width the list/detail divider actually moves within — the wrapper minus
  // Evidence (equals the wrapper when Evidence is closed).
  const workspaceWidth = Math.round((containerWidth * workspaceSharePct) / 100);

  // Drop any pending debounced measurement on unmount so a trailing timer can't
  // fire against a torn-down route.
  useEffect(
    () => () => {
      debouncedSetContainerWidth.cancel();
      debouncedSetWorkspaceShare.cancel();
    },
    [debouncedSetContainerWidth, debouncedSetWorkspaceShare]
  );

  // Resolves the split percentage from the saved preference (or the target px),
  // returning null when the container is unmeasured so callers defer rather than
  // guess — avoiding the old too-wide 40% fallback. Uses the WORKSPACE width so
  // the restored percent (applied to the inner list/detail group) matches the
  // width the drag ceiling used; before measurement it falls back to the wrapper
  // (equal to the workspace with Evidence closed, the common first-open case).
  const getSplitPercent = useCallback(
    (targetPx: number): number | null => {
      const width = workspaceWidthRef.current || panelGroupRef.current?.offsetWidth || 0;
      return computeSplitPercent(width, targetPx, readSavedSplitPx(storageKey));
    },
    [storageKey]
  );

  // Settle window + split/collapse appliers + the content slide pins (the
  // RHS-session-panel-style clip for programmatic collapse/expand).
  const {
    isProgrammaticResizeRef,
    markProgrammaticResize,
    applySplitLayout,
    applyInitialSplitLayout,
    collapseLeftPanel,
    leftContentRef,
    rightContentRef,
  } = useProgrammaticPanelResize({
    leftPanelRef,
    rightPanelRef,
    containerWidthRef,
    panelGroupRef,
    settleMs: PROGRAMMATIC_RESIZE_SETTLE_MS,
  });

  // The workspace panel reports its share of the outer group here (its % of the
  // list+detail+Evidence split); that share scales the wrapper width down to the
  // inner list+detail group. Fires on Evidence open/close/resize (a % change) — a
  // window resize keeps the % and is covered by the observer above instead.
  const onWorkspaceResize = useCallback(
    (sharePct: number) => {
      if (sharePct <= 0 || sharePct === workspaceSharePctRef.current) return;
      // A share change is Evidence opening/closing: it shrinks/grows the workspace
      // and the library clamps the list to the new ceiling — a forced resize, not
      // a user drag. Open the settle window so that clamp's `onResize` isn't
      // persisted over the user's saved width.
      markProgrammaticResize();
      workspaceSharePctRef.current = sharePct;
      workspaceWidthRef.current = ((containerWidthRef.current || 0) * sharePct) / 100;
      debouncedSetWorkspaceShare(sharePct);
    },
    [debouncedSetWorkspaceShare, markProgrammaticResize]
  );

  // Sourced from an atom so the sidebar can read it — see `sessionListCollapsedAtom`.
  // Unmount reset is handled inside `useSidebarSessionListBridge`.
  const [isLeftCollapsed, setIsLeftCollapsed] = useAtom(sessionListCollapsedAtom);
  // Mirror the collapse state into a ref so the restore effect can honour the
  // user's last collapse intent across a breakpoint round-trip (the split ⇄
  // single-pane remount) without taking `isLeftCollapsed` as an effect dep, which
  // would change when the effect fires and risk a set-in-effect loop.
  const isLeftCollapsedRef = useRef(isLeftCollapsed);
  isLeftCollapsedRef.current = isLeftCollapsed;
  const setEvidenceSidebarOpen = useSetAtom(evidenceSidebarOpenAtom);

  // Applies the restored split geometry. When the container width isn't
  // measurable yet (`getSplitPercent` → null) we can't compute the saved
  // percentage, so fall back to expanding both panels — this always reveals the
  // detail pane rather than leaving the right panel collapsed at its
  // `defaultSize` (0), and the `isContainerMeasured` re-run in the restore
  // effect applies the precise saved width once a measurement lands. Without
  // the fallback the right panel stays at 0 while the header/list flip to split
  // mode off the state-derived `isSplit`, leaving the detail invisible (the
  // regression from the conditional guard added in #11467). Callers own the
  // `isLeftCollapsed` flag; this only touches panel sizing.
  const openDetailSplit = useCallback(() => {
    const leftPct = getSplitPercent(DEFAULT_SPLIT_PX);
    if (leftPct != null) {
      applySplitLayout(leftPct);
      return;
    }
    markProgrammaticResize();
    leftPanelRef.current?.expand();
    rightPanelRef.current?.expand();
  }, [getSplitPercent, applySplitLayout, markProgrammaticResize]);

  const [isInitialLayoutReady, setIsInitialLayoutReady] = useState(
    isStableStartupFlagResolved && !isStableStartupEnabled
  );
  const hasCompletedInitialLayoutRef = useRef(
    isStableStartupFlagResolved && !isStableStartupEnabled
  );
  const completeInitialLayout = useCallback(() => {
    hasCompletedInitialLayoutRef.current = true;
    setIsInitialLayoutReady(true);
  }, []);
  /** Previous cover state, so a release-only rerun can be told apart. */
  const wasColdLoadCoverReleasedRef = useRef(hasColdLoadCoverReleased);
  const lastViewedSessionId = useLastViewedSessionId(sessionId);
  const pendingRestoreLayoutRef = useRef<'detail' | 'split' | null>(null);

  // Hiding from fullscreen navigates to the last session before it collapses, so
  // `isSplit` is briefly true and the split (sand) chrome would flash for the
  // pre-collapse frame and the 300ms slide. Hold the fullscreen chrome until the
  // slide settles; by then the list is hidden at 0 width, so restoring split
  // chrome/columns is invisible and a later expand won't rebuild columns mid-slide.
  const [isFullscreenDetailCollapse, setIsFullscreenDetailCollapse] = useState(false);
  const fullscreenCollapseTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const clearFullscreenDetailCollapse = useCallback(() => {
    if (fullscreenCollapseTimerRef.current) {
      clearTimeout(fullscreenCollapseTimerRef.current);
      fullscreenCollapseTimerRef.current = undefined;
    }
    setIsFullscreenDetailCollapse(false);
  }, []);
  useEffect(() => () => clearTimeout(fullscreenCollapseTimerRef.current), []);

  const panelActions = useMemo<PanelActions>(() => {
    const restoreLastViewedSession = (layout: 'detail' | 'split'): boolean => {
      if (hasDetail || !lastViewedSessionId) return false;

      pendingRestoreLayoutRef.current = layout;
      void router.push(`/scribe/sessions/${lastViewedSessionId}`);
      return true;
    };

    return {
      toDetail: () => {
        // From fullscreen list mode, wait for the session route before
        // collapsing; otherwise the visible list would disappear while the
        // detail pane is still empty. Hold the fullscreen chrome so the collapse
        // doesn't flash the sand background.
        if (restoreLastViewedSession('detail')) {
          setIsFullscreenDetailCollapse(true);
          return;
        }

        // Collapsing from split: chrome is already the split card, so no hold.
        // Flip the state up front so the detail header's layout control appears
        // on the click rather than ~300ms later, when the ResizablePanel's
        // onCollapse callback fires.
        clearFullscreenDetailCollapse();
        setIsLeftCollapsed(true);
        collapseLeftPanel();
        panelTracking.listClosed();
      },
      toList: () => {
        clearFullscreenDetailCollapse();
        setEvidenceSidebarOpen(false);
        void router.push('/scribe/sessions');
      },
      toSplit: () => {
        clearFullscreenDetailCollapse();
        setEvidenceSidebarOpen(false);
        if (restoreLastViewedSession('split')) return;

        setIsLeftCollapsed(false);
        openDetailSplit();
        panelTracking.listOpened();
      },
    };
  }, [
    router,
    hasDetail,
    lastViewedSessionId,
    setEvidenceSidebarOpen,
    setIsLeftCollapsed,
    clearFullscreenDetailCollapse,
    openDetailSplit,
    collapseLeftPanel,
    panelTracking,
  ]);

  // Apply the first visible geometry without a transition. A bare route stays
  // under the cover until session restoration either chooses a detail or makes
  // a terminal list-only decision.
  useEffect(() => {
    // The cover's release is a handoff signal for an UNFINISHED startup, never
    // a layout input. Once startup has applied its geometry, a rerun on the
    // release alone must not fall through to the legacy path below and animate
    // the layout the user is already looking at.
    const isCoverReleaseRerun =
      wasColdLoadCoverReleasedRef.current !== hasColdLoadCoverReleased;
    wasColdLoadCoverReleasedRef.current = hasColdLoadCoverReleased;
    if (isCoverReleaseRerun && hasCompletedInitialLayoutRef.current) return;

    if (!isStableStartupFlagResolved) return;

    if (!isStableStartupEnabled && !hasCompletedInitialLayoutRef.current) {
      hasCompletedInitialLayoutRef.current = true;
      setIsInitialLayoutReady(true);
    }

    if (isStableStartupEnabled && !hasCompletedInitialLayoutRef.current) {
      // Startup must never fall through to the animated restoration below.
      // Wait under the cover, then apply exactly one final geometry directly.
      const isStartupBlocked =
        !isContainerMeasured ||
        !isSplitStorageReady ||
        (!hasDetail && isColdRestorePending);
      // Still covered — keep waiting, so the first visible frame is final.
      if (isStartupBlocked && !hasColdLoadCoverReleased) return;
      if (isStartupBlocked) {
        // The cover's deadline released it without these settling. Hand this
        // mount to the legacy layout below, which owns the deferred-measurement
        // reveal, and open the persistence guard so a user resize saves. Late
        // layout movement is the accepted cost of the deadline.
        hasCompletedInitialLayoutRef.current = true;
        setIsInitialLayoutReady(true);
      } else {
        if (!canFitSplit) {
          completeInitialLayout();
          return;
        }
        if (hasDetail && !isEhrLaunch && !isLeftCollapsedRef.current) {
          const leftPct = getSplitPercent(DEFAULT_SPLIT_PX);
          if (leftPct == null) return;
          setIsLeftCollapsed(false);
          applyInitialSplitLayout(leftPct, completeInitialLayout);
          return;
        }
        if (hasDetail) {
          setIsLeftCollapsed(true);
          applyInitialSplitLayout(0, completeInitialLayout);
          return;
        }
        setIsLeftCollapsed(false);
        setEvidenceSidebarOpen(false);
        applyInitialSplitLayout(100, completeInitialLayout);
        return;
      }
    }

    if (hasDetail) {
      const pendingRestoreLayout = pendingRestoreLayoutRef.current;
      if (pendingRestoreLayout) {
        pendingRestoreLayoutRef.current = null;
        if (pendingRestoreLayout === 'detail') {
          setIsLeftCollapsed(true);
          collapseLeftPanel();
          panelTracking.listClosed();
          // Release the hold once the collapse slide settles. Armed here (at
          // collapse start), not at the click, so route latency can't drop it
          // before the list finishes sliding out.
          if (fullscreenCollapseTimerRef.current) {
            clearTimeout(fullscreenCollapseTimerRef.current);
          }
          fullscreenCollapseTimerRef.current = setTimeout(
            () => setIsFullscreenDetailCollapse(false),
            PROGRAMMATIC_RESIZE_SETTLE_MS
          );
        } else {
          setIsLeftCollapsed(false);
          openDetailSplit();
        }
        return;
      }

      // EHR launch always opens collapsed. Otherwise honour the user's last
      // collapse intent so a breakpoint round-trip (the split ⇄ single-pane
      // remount, which re-runs this effect via `canFitSplit`) preserves a
      // collapsed list instead of springing it back open (SCR-5726).
      // Re-applying the collapse — rather than only keeping the flag — collapses
      // the freshly remounted list panel to 0 so it lands interactive rather than
      // visible-but-`inert` at its full `defaultSize` (the mobile round-trip case
      // the previous unconditional reset guarded against). The open path uses
      // `openDetailSplit`, which owns the deferred-measurement fallback (expand
      // both panels until a width lands) so the detail always reveals rather than
      // staying at the right panel's `defaultSize` 0.
      if (isEhrLaunch || isLeftCollapsedRef.current) {
        setIsLeftCollapsed(true);
        collapseLeftPanel();
      } else {
        setIsLeftCollapsed(false);
        openDetailSplit();
      }
    } else {
      // A fullscreen-list menu action is navigating back to the last session.
      // Preserve its requested layout until that route id arrives.
      if (pendingRestoreLayoutRef.current) return;

      setIsLeftCollapsed(false);
      setEvidenceSidebarOpen(false);
      applySplitLayout(100);
    }
  }, [
    hasDetail,
    isEhrLaunch,
    isSplitStorageReady,
    isColdRestorePending,
    isStableStartupEnabled,
    isStableStartupFlagResolved,
    hasColdLoadCoverReleased,
    setEvidenceSidebarOpen,
    setIsLeftCollapsed,
    openDetailSplit,
    applySplitLayout,
    collapseLeftPanel,
    panelTracking,
    isContainerMeasured,
    completeInitialLayout,
    applyInitialSplitLayout,
    getSplitPercent,
    // Re-apply the split geometry when the container widens back into split
    // range — the panel group remounts (defaulting the right pane to 0), which
    // also covers the mobile breakpoint now that it routes through single-pane.
    canFitSplit,
  ]);

  // Right-hand control panels may coexist with the session list. Opening the
  // Evidence / Ask Heidi sidebar must not mutate the user's left-panel state.
  // The page isolates Evidence in an outer resizable group so its geometry
  // cannot expand the list. Only explicit panelActions close Evidence; resize
  // callbacks describe inner list/detail geometry, not user intent for the RHS.
  const contextValue = useMemo<SessionsPanelContextValue>(
    () => ({
      sessionId,
      lastViewedSessionId,
      isLeftCollapsed,
      isEhrLaunch,
      canFitSplit,
      isFullscreenDetailCollapse,
      panelActions,
    }),
    [
      sessionId,
      lastViewedSessionId,
      isLeftCollapsed,
      isEhrLaunch,
      canFitSplit,
      isFullscreenDetailCollapse,
      panelActions,
    ]
  );

  const onLeftCollapse = useCallback(() => {
    if (!hasDetail) return;
    setIsLeftCollapsed(true);
    // Programmatic collapses (toDetail, sidebar coordination, EHR launch)
    // track their own panel events or deliberately stay silent.
    if (!isProgrammaticResizeRef.current) panelTracking.listClosed();
  }, [hasDetail, setIsLeftCollapsed, panelTracking, isProgrammaticResizeRef]);
  const onLeftExpand = useCallback(() => {
    clearFullscreenDetailCollapse();
    setIsLeftCollapsed(false);
    if (!isProgrammaticResizeRef.current) panelTracking.listOpened();
  }, [
    clearFullscreenDetailCollapse,
    setIsLeftCollapsed,
    panelTracking,
    isProgrammaticResizeRef,
  ]);

  // Persists the split width on drag/keyboard resize (no drag-to-close — see the hook).
  const splitCloseControls = useSessionsSplitClose({
    storageKey,
    workspaceWidthRef,
    isProgrammaticResizeRef,
    isPersistenceReadyRef: hasCompletedInitialLayoutRef,
  });

  return {
    sessionId,
    canFitSplit,
    isInitialLayoutReady,
    workspaceWidth,
    onWorkspaceResize,
    leftPanelRef,
    leftContentRef,
    rightPanelRef,
    rightContentRef,
    panelGroupRef,
    panelGroupEl,
    setPanelGroupRef,
    contextValue,
    onLeftCollapse,
    onLeftExpand,
    ...splitCloseControls,
  };
};

// Three-state layout (list/split/detail). Returns inert defaults when the
// new-list flag is off or no provider is mounted, so legacy callers under
// `/scribe/session/[id]` are unaffected.
export const useSessionsPanelLayout = () => {
  const isEnabled = useIsNewSessionListEnabled();
  const ctx = useContext(SessionsPanelContext);
  if (!isEnabled || !ctx) {
    return {
      sessionId: '',
      lastViewedSessionId: '',
      isSplit: false,
      isDetail: false,
      isSinglePane: false,
      isFullscreenDetailCollapse: false,
      panelActions: null as PanelActions | null,
    };
  }
  return {
    sessionId: ctx.sessionId,
    lastViewedSessionId: ctx.lastViewedSessionId,
    isSplit: !!ctx.sessionId && !ctx.isLeftCollapsed,
    isDetail: !!ctx.sessionId && ctx.isLeftCollapsed,
    isFullscreenDetailCollapse: ctx.isFullscreenDetailCollapse,
    // True when the container is too narrow for both panes, so consumers can
    // adopt the mobile single-pane chrome (e.g. the back-to-list header).
    isSinglePane: !ctx.canFitSplit,
    panelActions: ctx.isEhrLaunch ? null : ctx.panelActions,
  };
};
